/**
 * The management fee the OM stated, carried as a RATE without losing the dollars.
 *
 * 🚨 13368 MAIN STREET LOST $113 TO A ROUNDING CALL. The OM states "Management Fee equal to 3% of
 * scheduled base rent" - $8,157. The model holds the fee as a PERCENTAGE, so the seeder
 * back-calculates one against the income base and stored it at `.toFixed(1)`:
 *
 *     8,157 / 383,024 = 2.1296%  ->  stored 2.1%  ->  applied 2.1% x 383,024 = $8,044
 *
 * $113 short of the figure printed in the document, on the one line a buyer reconciles against the
 * OM. ⭐⭐ **A rate is a REPRESENTATION of the fee; the document's dollars are the fee.** Four
 * decimals reproduce them exactly here ($8,157) and one decimal cannot.
 *
 * ⚠️ THE RATE IS STILL THE SINGLE SOURCE. `management_fee_amount` exists on the row and on the
 * type and is read by NOTHING - adding a second field the calculators would have to agree with is
 * how two surfaces come to print different fees. Precision, not a parallel value.
 */

/** Enough decimals to reproduce the stated dollars; few enough to stay legible in an input. */
const RATE_DP = 4;

/**
 * The rate that reproduces `amount` against `base`, or null when it cannot be expressed as one.
 *
 * ⛔ Null rather than 0 for a non-positive base: 0% is a real assumption ("the landlord manages it
 * himself"), and a fee that silently became zero is exactly the class of thing that reaches a
 * contract unnoticed. The caller decides what to do with an unrepresentable fee.
 */
export const managementFeeRate = (amount: number, base: number): number | null => {
  if (!Number.isFinite(amount) || !Number.isFinite(base)) return null;
  if (amount <= 0 || base <= 0) return null;
  return parseFloat(((amount / base) * 100).toFixed(RATE_DP));
};

/**
 * How that rate reads on a line item: "3%", "2.13%", "4.5%".
 *
 * ⭐ Trailing zeros are trimmed so a whole-number preset still prints "4%" rather than "4.00%",
 * and the back-calculated rate prints "2.13%" rather than its full four decimals. The LABEL is
 * rounded; the arithmetic underneath is not.
 */
export const formatFeeRate = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '0%';
  return `${parseFloat(rate.toFixed(2))}%`;
};
